import { useEffect, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { Button, Card, Input, Modal, StatusPill, EmptyState, useToast } from '../../design-system';
import '../styles/Portals.css';

const MANAGER_LEVEL = 3;

const EMPTY = {
  code: '',
  displayName: '',
  loginUrl: '',
  jurisdiction: 'IN',
  requiresCaptcha: false,
  requiresOtp: false,
  isActive: true,
};

// The portals an org can store credentials against. Global rows (the platform
// catalog, e.g. GST) are shown read-only; the org manages its own additions.
// These are exactly what appears in the "Add Login" portal dropdown under
// Credentials — auto-login also needs a recipe, which only global rows have for
// now, so org-added portals are "store & reveal" until a recipe is configured.
function PortalsManager() {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = (user?.role?.level || 0) >= MANAGER_LEVEL;

  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    apiClient
      .get('/api/portals', { params: { manage: 'true' } })
      .then((res) => setPortals(res.data))
      .catch(() => toast.danger('Failed to load portals'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p._id);
    setForm({
      code: p.code,
      displayName: p.displayName,
      loginUrl: p.loginUrl,
      jurisdiction: p.jurisdiction || 'IN',
      requiresCaptcha: !!p.requiresCaptcha,
      requiresOtp: !!p.requiresOtp,
      isActive: p.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return setError('Code is required');
    if (!form.displayName.trim()) return setError('Name is required');
    if (!/^https:\/\/\S+/i.test(form.loginUrl.trim())) return setError('Login URL must be an https URL');
    setSaving(true);
    setError('');
    try {
      if (editingId) await apiClient.put(`/api/portals/${editingId}`, form);
      else await apiClient.post('/api/portals', form);
      setModalOpen(false);
      toast.success(editingId ? 'Portal updated' : 'Portal added');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save portal');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the portal "${p.displayName}"?`)) return;
    try {
      await apiClient.delete(`/api/portals/${p._id}`);
      toast.success('Portal deleted');
      load();
    } catch (err) {
      // 409 = credentials still use it — the message suggests deactivating.
      toast.danger(err.response?.data?.error || 'Failed to delete portal');
    }
  };

  return (
    <Card>
      <Card.Header
        title="🔐 Credential Portals"
        subtitle="Portals you can store client logins against (shown in the Add Login picker)"
        action={
          canEdit ? (
            <Button type="button" variant="primary" size="sm" onClick={openAdd}>
              + Add Portal
            </Button>
          ) : null
        }
      />

      {loading ? (
        <p className="portal-hint">Loading…</p>
      ) : portals.length === 0 ? (
        <EmptyState icon="🔐" title="No portals" description="Add a portal to store client logins against it." />
      ) : (
        <div className="portal-list">
          {portals.map((p) => (
            <div key={p._id} className="portal-row">
              <div className="portal-identity">
                <span className="portal-name">
                  {p.displayName}
                  <span className="portal-code">{p.code}</span>
                </span>
                <a className="portal-url" href={p.loginUrl} target="_blank" rel="noreferrer">
                  {p.loginUrl}
                </a>
              </div>

              <div className="portal-flags">
                <StatusPill variant={p.scope === 'global' ? 'neutral' : 'info'} label={p.scope === 'global' ? 'Global' : 'Org'} />
                <StatusPill variant={p.isActive ? 'success' : 'neutral'} label={p.isActive ? 'Active' : 'Inactive'} />
                {p.requiresCaptcha && <span className="portal-tag">Captcha</span>}
                {p.requiresOtp && <span className="portal-tag">OTP</span>}
                <span className={`portal-tag ${p.hasRecipe ? 'is-on' : 'is-off'}`}>
                  {p.hasRecipe ? 'Auto-login' : 'Store only'}
                </span>
              </div>

              <div className="portal-actions">
                {p.editable ? (
                  <>
                    <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(p)}>
                      Edit
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(p)}>
                      Delete
                    </Button>
                  </>
                ) : (
                  <span className="portal-managed" title="Managed by the platform admin">
                    🔒 Managed
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <Modal
          isOpen
          onClose={() => setModalOpen(false)}
          title={editingId ? 'Edit portal' : 'Add portal'}
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={save} loading={saving}>
                {editingId ? 'Save' : 'Add'}
              </Button>
            </>
          }
        >
          <form className="portal-form" onSubmit={save}>
            <div className="portal-form-row">
              <Input
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. INCOME_TAX"
                helperText="Short unique code"
              />
              <Input
                label="Name"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="e.g. Income Tax Portal"
              />
            </div>
            <Input
              label="Login URL"
              value={form.loginUrl}
              onChange={(e) => setForm({ ...form, loginUrl: e.target.value })}
              placeholder="https://…"
            />
            <div className="portal-checks">
              <label className="portal-check">
                <input
                  type="checkbox"
                  checked={form.requiresCaptcha}
                  onChange={(e) => setForm({ ...form, requiresCaptcha: e.target.checked })}
                />
                Requires captcha
              </label>
              <label className="portal-check">
                <input
                  type="checkbox"
                  checked={form.requiresOtp}
                  onChange={(e) => setForm({ ...form, requiresOtp: e.target.checked })}
                />
                Requires OTP
              </label>
              {editingId && (
                <label className="portal-check">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active (shown in the picker)
                </label>
              )}
            </div>
            <p className="portal-hint">
              Auto-login (the agent filling this portal) needs a login recipe configured by the platform admin. New
              portals are usable for storing and revealing logins right away.
            </p>
            {error && <p className="portal-form-error">{error}</p>}
          </form>
        </Modal>
      )}
    </Card>
  );
}

export default PortalsManager;
